import { Checkbox, ControlledCheckbox } from "@dune/ui";
import { useEffect, useState } from "react";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import { ConstructionSiteDetailsFormData } from "./useConstructionSiteDetailsForm";

type ControlledInterventionCheckboxType = {
	label: string;
	name: string;
};

type InterventionCategoryCheckboxType = {
	label: string;
	name: keyof ConstructionSiteDetailsFormData["interventions"];
	checked: boolean;
	subCheckboxes: ControlledInterventionCheckboxType[];
};

type InterventionSubCategory = {
	[key: string]: boolean;
};

interface InterventionsDetailsContainerProps {
	isDisabled?: boolean;
}

const InterventionsDetailsContainer = ({
	isDisabled = false,
}: InterventionsDetailsContainerProps) => {
	const {
		control,
		setValue,
		formState: { errors },
		getValues,
	} = useFormContext<ConstructionSiteDetailsFormData>();

	const interventions = useWatch({ control, name: "interventions" });
	const [checkboxes, setCheckboxes] = useState<
		InterventionCategoryCheckboxType[]
	>([
		{
			label: "Architecte",
			name: "architect",
			checked: Object.values(getValues("interventions.architect")).some(
				(value) => value,
			),
			subCheckboxes: [
				{ label: "Conception", name: "ARCHITECT_CONCEPTION" },
				{
					label: "Dépôt d'autorisation administrative",
					name: "ARCHITECT_BUILDING_PERMIT_DEPOT",
				},
				{
					label: "Suivi de travaux",
					name: "ARCHITECT_WORK_FOLLOW_UP",
				},
			],
		},
		{
			label: "Maîtrise d'oeuvre",
			name: "moe",
			checked: Object.values(getValues("interventions.moe")).some(
				(value) => value,
			),
			subCheckboxes: [
				{ label: "Conception", name: "MOE_CONCEPTION" },
				{
					label: "Dépôt d'autorisation administrative",
					name: "MOE_BUILDING_PERMIT_DEPOT",
				},
				{
					label: "Suivi de travaux",
					name: "MOE_WORK_FOLLOW_UP",
				},
			],
		},
		{
			label: "Étude de sol",
			name: "soil_study",
			checked: Object.values(getValues("interventions.soil_study")).some(
				(value) => value,
			),
			subCheckboxes: [
				{ label: "G2AVP", name: "G2AVP" },
				{ label: "G2PRO", name: "G2PRO" },
				{ label: "G3", name: "G3" },
				{ label: "G4", name: "G4" },
				{ label: "G5", name: "G5" },
			],
		},
		{
			label: "Contrôle technique",
			name: "technical_control",
			checked: Object.values(getValues("interventions.technical_control")).some(
				(value) => value,
			),
			subCheckboxes: [
				{
					label:
						"L : Solidité des ouvrages constitutifs ou indissociables au bâtiment",
					name: "L",
				},
				{
					label: "LP : Solidité des ouvrages indissociables et dissociables",
					name: "LP",
				},
				{
					label: "LE : Solidité des existants",
					name: "LE",
				},
				{
					label: "AV: vérification des Avoisinants",
					name: "AV",
				},
				{
					label:
						"PS : Sécurité des personnes dans les constructions en cas de Séisme",
					name: "PS",
				},
				{
					label: "S : Sécurité des personnes dans la construction",
					name: "S",
				},
				{
					label:
						"SH : Sécurité incendie des personnes dans les bâtiments d'habitation",
					name: "SH",
				},
				{
					label:
						"STI : Sécurité incendie des personnes dans les bâtiments relevant du code du travail uniquement",
					name: "STI",
				},
				{
					label:
						"SEI : Sécurité incendie des personnes dans les établissements recevant du public",
					name: "SEI",
				},
				{
					label:
						"HAND : Vérification des exigences d'accessibilité des personnes handicapées",
					name: "HAND",
				},
				{
					label: "BRD : Passage du brancard",
					name: "BRD",
				},
				{
					label:
						"Th : Vérification des exigences d'isolation thermique et aux économies d'énergie des bâtiments",
					name: "TH",
				},
				{
					label:
						"PHh : Vérification des exigences d'isolation acoustiques dans les bâtiments d'habitation",
					name: "PHH",
				},
				{
					label:
						"PHa : Vérification des exigences d'isolation acoustique dans les bâtiments autre qu'habitation",
					name: "PHA",
				},
				{
					label:
						"PV : Recollement des procès-verbaux d'essais de fonctionnement des installations",
					name: "PV",
				},
				{
					label: "F : Fonctionnement des installations",
					name: "F",
				},
			],
		},
		{
			label: "Bureau d'études",
			name: "consulting",
			checked: Object.values(getValues("interventions.consulting")).some(
				(value) => value,
			),
			subCheckboxes: [
				{ label: "Structure", name: "STRUCTURAL" },
				{ label: "Acoustique", name: "ACOUSTIC" },
				{ label: "Assainissement", name: "SANITATION" },
				{ label: "Infiltration", name: "INFILTRATION" },
				{ label: "Géotechnique", name: "GEOTECHNICAL" },
				{ label: "Thermique", name: "THERMAL" },
				{ label: "Fluide", name: "FLUID" },
				{
					label: "VRD (Voirie et Réseaux Divers)",
					name: "VRD",
				},
				{
					label: "HQE/Développement durable",
					name: "HQE",
				},
				{ label: "Éclairage", name: "LIGHTING" },
				{
					label: "Sécurité incendie",
					name: "FIRE_SAFETY",
				},
			],
		},
	]);

	useEffect(() => {
		if (interventions) {
			setCheckboxes((prevCheckboxes) =>
				prevCheckboxes.map((checkbox) => {
					const categoryData = interventions[checkbox.name];
					if (categoryData && typeof categoryData === "object") {
						const subCategoryData = categoryData as InterventionSubCategory;
						const isGroupChecked =
							checkbox.checked ||
							checkbox.subCheckboxes.some((subCheckbox) => {
								const isChecked = subCategoryData[subCheckbox.name] === true;
								return isChecked;
							});
						return {
							...checkbox,
							checked: isGroupChecked,
						};
					}
					return {
						...checkbox,
						checked: false,
					};
				}),
			);
		}
	}, [interventions]);

	const error = errors.interventions;

	return (
		<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4">
			{!isDisabled && "Interventions prévues"}
			{error?.message && (
				<span className="text-red-500 text-sm">
					{error?.message?.toString()}
				</span>
			)}
			<div className="grid grid-cols-3 gap-4">
				{checkboxes.map((checkbox) => (
					<div className="flex flex-col" key={checkbox.name}>
						<Checkbox
							id={checkbox.label}
							label={checkbox.label}
							checked={checkbox.checked}
							disabled={isDisabled}
							onCheckedChange={(e) => {
								setCheckboxes(
									(previousCheckboxes: InterventionCategoryCheckboxType[]) =>
										previousCheckboxes.map(
											(previousCheckbox: InterventionCategoryCheckboxType) => {
												if (previousCheckbox.name === checkbox.name) {
													if (!e) {
														previousCheckbox.subCheckboxes.map(
															(subCheckbox) => {
																setValue(
																	`interventions.${checkbox.name}.${subCheckbox.name}` as FieldPath<ConstructionSiteDetailsFormData>,
																	false,
																);
															},
														);
													}
													return {
														...previousCheckbox,
														checked: e === "indeterminate" ? false : e,
													};
												}
												return previousCheckbox;
											},
										),
								);
							}}
							className="border-gray-300 disabled:bg-gray-100"
						/>
						<div className="flex flex-col items-start gap-2">
							{checkbox.checked &&
								checkbox.subCheckboxes.map(
									(subCheckbox: ControlledInterventionCheckboxType) => (
										<ControlledCheckbox
											id={subCheckbox.name}
											key={subCheckbox.name}
											label={subCheckbox.label}
											control={control}
											name={
												`interventions.${checkbox.name}.${subCheckbox.name}` as FieldPath<ConstructionSiteDetailsFormData>
											}
											disabled={isDisabled}
										/>
									),
								)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default InterventionsDetailsContainer;
